/**
 * Lightweight predictive search dropdown for the header.
 *
 * Wraps a search form and shows product suggestions (image + title + price)
 * below the input as the user types. Uses the public /search/suggest.json
 * endpoint so it never collides with Horizon's modal predictive search.
 */
class HeaderPredictiveSearch extends HTMLElement {
  static DEBOUNCE_MS = 220;
  static MIN_QUERY_LENGTH = 2;
  static MAX_RESULTS = 8;

  connectedCallback() {
    this.input = this.querySelector('input[name="q"]');
    this.panel = this.querySelector('[data-results-panel]');
    this.list = this.querySelector('[data-results-list]');
    this.status = this.querySelector('[data-status]');
    this.viewAll = this.querySelector('[data-view-all]');
    this.viewAllText = this.querySelector('[data-view-all-text]');

    if (!this.input || !this.panel || !this.list) return;

    this._abort = new AbortController();
    const { signal } = this._abort;

    this.input.addEventListener('input', this.onInput, { signal });
    this.input.addEventListener('focus', this.onFocus, { signal });
    this.input.addEventListener('keydown', this.onKeydown, { signal });
    document.addEventListener('click', this.onDocumentClick, { signal });
    this.viewAllBaseHref = this.viewAll?.getAttribute('href') || '/search';
  }

  disconnectedCallback() {
    this._abort?.abort();
    clearTimeout(this._debounceTimer);
    this._fetchAbort?.abort();
  }

  onInput = () => {
    clearTimeout(this._debounceTimer);
    const q = this.input.value.trim();
    if (q.length < HeaderPredictiveSearch.MIN_QUERY_LENGTH) {
      this.hide();
      return;
    }
    this._debounceTimer = setTimeout(() => this.fetchSuggestions(q), HeaderPredictiveSearch.DEBOUNCE_MS);
  };

  onFocus = () => {
    const q = this.input.value.trim();
    if (q.length >= HeaderPredictiveSearch.MIN_QUERY_LENGTH && this.list.children.length > 0) {
      this.show();
    }
  };

  onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.hide();
  };

  onKeydown = (event) => {
    const items = Array.from(this.list.querySelectorAll('a'));
    const isOpen = !this.panel.hidden;

    switch (event.key) {
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          this.hide();
          this.input.blur();
        }
        return;
      case 'ArrowDown':
        if (!isOpen || items.length === 0) return;
        event.preventDefault();
        this.moveFocus(items, 1);
        return;
      case 'ArrowUp':
        if (!isOpen || items.length === 0) return;
        event.preventDefault();
        this.moveFocus(items, -1);
        return;
    }
  };

  moveFocus(items, dir) {
    const current = document.activeElement;
    let idx = items.indexOf(current);
    if (idx === -1) {
      idx = dir > 0 ? 0 : items.length - 1;
    } else {
      idx = (idx + dir + items.length) % items.length;
    }
    items[idx]?.focus();
  }

  async fetchSuggestions(query) {
    this._fetchAbort?.abort();
    this._fetchAbort = new AbortController();

    const base = (window.Theme?.routes?.predictive_search_url) || '/search/suggest';
    const url = new URL(base + '.json', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(HeaderPredictiveSearch.MAX_RESULTS));
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    this.setStatus('Searching…');

    try {
      const res = await fetch(url.toString(), { signal: this._fetchAbort.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('Predictive search request failed: ' + res.status);
      const data = await res.json();
      const products = data?.resources?.results?.products || [];
      this.renderResults(products, query);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[header-predictive-search]', err);
      this.setStatus('Something went wrong. Press Enter to search.');
      this.list.innerHTML = '';
      this.show();
    }
  }

  setStatus(text) {
    if (!this.status) return;
    if (!text) {
      this.status.hidden = true;
      this.status.textContent = '';
    } else {
      this.status.hidden = false;
      this.status.textContent = text;
    }
  }

  renderResults(products, query) {
    this.list.innerHTML = '';

    if (this.viewAll) {
      const viewAllUrl = new URL(this.viewAllBaseHref, window.location.origin);
      viewAllUrl.searchParams.set('q', query);
      this.viewAll.setAttribute('href', viewAllUrl.pathname + viewAllUrl.search);
    }

    if (!products.length) {
      this.setStatus(`No products found for “${query}”.`);
      if (this.viewAll) this.viewAll.hidden = true;
      this.show();
      return;
    }

    this.setStatus(null);

    const frag = document.createDocumentFragment();
    for (const p of products) {
      const li = document.createElement('li');
      li.className = 'ch-predictive-search__item';

      const a = document.createElement('a');
      a.className = 'ch-predictive-search__link';
      a.href = p.url || '#';
      a.setAttribute('role', 'option');

      // Image
      const imgWrap = document.createElement('div');
      imgWrap.className = 'ch-predictive-search__img';
      const imgUrl = this.getImageUrl(p);
      if (imgUrl) {
        const img = document.createElement('img');
        img.src = imgUrl;
        img.alt = p.featured_image?.alt || p.title || '';
        img.loading = 'lazy';
        img.width = 80;
        img.height = 80;
        imgWrap.appendChild(img);
      }

      // Info
      const info = document.createElement('div');
      info.className = 'ch-predictive-search__info';

      const title = document.createElement('span');
      title.className = 'ch-predictive-search__title';
      title.textContent = p.title || '';
      info.appendChild(title);

      if (p.vendor) {
        const vendor = document.createElement('span');
        vendor.className = 'ch-predictive-search__vendor';
        vendor.textContent = p.vendor;
        info.appendChild(vendor);
      }

      const price = document.createElement('span');
      price.className = 'ch-predictive-search__price';
      price.innerHTML = this.buildPriceHTML(p);
      info.appendChild(price);

      a.append(imgWrap, info);
      li.appendChild(a);
      frag.appendChild(li);
    }
    this.list.appendChild(frag);
    if (this.viewAll) this.viewAll.hidden = false;
    this.show();
  }

  getImageUrl(product) {
    const fi = product.featured_image;
    if (typeof fi === 'string') return this.sizeImage(fi, 160);
    if (fi?.url) return this.sizeImage(fi.url, 160);
    if (product.image) return this.sizeImage(product.image, 160);
    return null;
  }

  /** Append a width query so we don't load full-resolution product imagery. */
  sizeImage(src, width) {
    if (!src) return src;
    try {
      const url = new URL(src, window.location.origin);
      // Shopify CDN supports the ?width= param.
      if (/cdn\.shopify\.com|shopifycdn\.com/.test(url.hostname)) {
        url.searchParams.set('width', String(width));
        return url.toString();
      }
      return src;
    } catch {
      return src;
    }
  }

  buildPriceHTML(product) {
    const symbol = window.Shopify?.currency?.active || '';
    const priceStr = product.price ?? product.price_min ?? '';
    const compareStr = product.compare_at_price_max ?? product.compare_at_price ?? '';

    const formatted = this.formatMoney(priceStr);
    if (!formatted) return '';

    let html = `<span class="ch-predictive-search__price-current">${this.escape(formatted)}</span>`;
    if (compareStr) {
      const cmp = this.formatMoney(compareStr);
      const priceNum = Number(priceStr);
      const cmpNum = Number(compareStr);
      if (cmp && !Number.isNaN(priceNum) && !Number.isNaN(cmpNum) && cmpNum > priceNum) {
        html += ` <span class="ch-predictive-search__price-compare">${this.escape(cmp)}</span>`;
      }
    }
    return html;
  }

  /**
   * The /search/suggest.json endpoint returns prices as plain decimal strings
   * (e.g. "29.95"). Format using the storefront's money format if exposed,
   * otherwise fall back to Intl with the active currency.
   */
  formatMoney(value) {
    if (value === '' || value == null) return '';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);

    const currency = window.Shopify?.currency?.active;
    if (currency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
        }).format(num);
      } catch {
        // fall through
      }
    }
    return num.toFixed(2);
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  show() {
    this.panel.hidden = false;
    this.classList.add('is-open');
  }

  hide() {
    this.panel.hidden = true;
    this.classList.remove('is-open');
  }
}

if (!customElements.get('header-predictive-search')) {
  customElements.define('header-predictive-search', HeaderPredictiveSearch);
}
